// Frontend-only translator: uses LibreTranslate (preferred) or MyMemory fallback.
// Caches in memory + localStorage (30 days).

const LT_URL = "http://192.168.100.80:5000/translate";

const mem = new Map();
const TTL = 30 * 24 * 60 * 60 * 1000; // 30 days

const key = (text, lang, src = "en") => `${src}->${lang}::${text}`;
const now = () => Date.now();

function getCached(k) {
  const hit = mem.get(k);
  if (hit && now() - hit.t < TTL) return hit.v;
  const raw = localStorage.getItem(k);
  if (!raw) return null;
  try {
    const e = JSON.parse(raw);
    if (now() - e.t < TTL) {
      mem.set(k, e);
      return e.v;
    }
    localStorage.removeItem(k);
  } catch {}
  return null;
}
function setCached(k, v) {
  const e = { v, t: now() };
  mem.set(k, e);
  try {
    localStorage.setItem(k, JSON.stringify(e));
  } catch {}
}

// normalize just in case
const norm = (l) => (l || "en").slice(0, 2); // "zh","hi","bn","ta","es","en"

/** translate one string */
export async function translateClient(text, targetLang, sourceLang = "en") {
  if (!text) return text;
  const tgt = norm(targetLang);
  const src = norm(sourceLang);
  if (tgt === src) return text;

  const k = key(text, tgt, src);
  const c = getCached(k);
  if (c) return c;

  // Try LibreTranslate (if running locally / CORS-allowed)
  try {
    const res = await fetch(LT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        q: text,
        source: src,
        target: tgt,
        format: "text",
      }),
    });
    if (res.ok) {
      const data = await res.json();
      const out = data?.translatedText ?? text;
      setCached(k, out);
      return out;
    }
  } catch {}

  // Fallback to MyMemory (no key)
  try {
    const u = new URL("https://api.mymemory.translated.net/get");
    u.searchParams.set("q", text);
    u.searchParams.set("langpair", `${src}|${tgt}`);
    const r = await fetch(u.toString());
    const d = await r.json();
    const out = d?.responseData?.translatedText ?? text;
    setCached(k, out);
    return out;
  } catch {}

  return text; // graceful fallback
}

/** translate many (dedupe + small parallel) -> Map<original, translated> */
export async function translateManyClient(
  strings,
  targetLang,
  sourceLang = "en"
) {
  const uniq = Array.from(
    new Set(strings.filter((s) => !!s && typeof s === "string"))
  );
  const map = new Map();
  const pending = [];

  const tgt = norm(targetLang);
  const src = norm(sourceLang);

  for (const s of uniq) {
    const c = getCached(key(s, tgt, src));
    if (c) map.set(s, c);
    else pending.push(s);
  }

  const translated = await Promise.all(
    pending.map((s) => translateClient(s, tgt, src))
  );
  translated.forEach((t, i) => map.set(pending[i], t));
  return map;
}
